import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { PERMISSION_ACTION_ADD, PERMISSION_ENTITY_TRADER, ROUTE_TRADER } from '@klear/constants'
import {
  KLEAR_ACCOUNT_TYPE_OPERATIONAL,
  KLEAR_ACCOUNT_TYPE_PAYABLE,
  KLEAR_ACCOUNT_TYPE_RECEIVABLE,
  klearAccountTypeLabel
} from '@klear/klear-financial-account/constants'
import { STRIPE_MCC_MAP } from '@klear/constants/industries'
import {
  EMPTY_TRADER_FORM,
  traderReadToForm,
  traderWireValidationToFormErrors,
  type TraderForm,
  type TraderFormDirty
} from '@klear/trader/wire.client'
import { wireValidationErrorMapFromTrpcError } from '@klear/schema/wire/trpc-form-errors'
import { parseTimestamp } from '@klear/time/boundary'
import { useForm, type FieldPath } from 'react-hook-form'
import { FormProvider } from 'react-hook-form'
import { X } from 'lucide-react'
import TraderOrganizationForm from '@/app/trader/TraderOrganizationForm'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Button } from '@klear/ui/components/button'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { useKyc } from '@klear/ui/hooks/use-kyc'
import { useAdminKlearFinancialAccounts } from '@klear/ui/hooks/use-klear-financial-account'
import type { TraderExternalBankAccount } from '@klear/trader-external-bank-account'
import isError from 'lodash/isError'
import toPairs from 'lodash/toPairs'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const TraderDetail: React.FC = () => {
  const { user: currentUser } = useAuth()
  const { traderId } = useParams()
  const navigate = useNavigate()
  const isEditQueryParam = new URLSearchParams(window.location.search).get('edit') === '1'
  const isNewTrader = !traderId || traderId === 'new'

  const { trader, isLoading: traderLoading, error: traderError } = useTrader(traderId)
  useDocumentTitle(trader?.name, isNewTrader ? 'New Trader' : 'Trader')

  const externalBankAccounts: TraderExternalBankAccount[] = []
  const {
    accounts: klearAccounts,
    isLoading: klearAccountsLoading,
    createAccount,
    isCreating: isCreatingKlearAccount
  } = useAdminKlearFinancialAccounts(trader?.organizationId)
  const hasReceivable = klearAccounts.some((a) => a.accountType === KLEAR_ACCOUNT_TYPE_RECEIVABLE)
  const hasPayable = klearAccounts.some((a) => a.accountType === KLEAR_ACCOUNT_TYPE_PAYABLE)
  const hasOperational = klearAccounts.some((a) => a.accountType === KLEAR_ACCOUNT_TYPE_OPERATIONAL)
  const { kyc } = useKyc()

  const { createTrader, updateTrader } = useTrader(traderId)

  const canEditTrader = () => {
    return currentUser.hasPermission(PERMISSION_ENTITY_TRADER, PERMISSION_ACTION_ADD)
  }

  const [editMode, setEditMode] = React.useState((isNewTrader || isEditQueryParam) && canEditTrader())

  const formValues = useMemo(() => {
    if (trader?.id && !isNewTrader) {
      return traderReadToForm(trader)
    }
    return EMPTY_TRADER_FORM
  }, [trader, isNewTrader])

  const traderForm = useForm<TraderForm>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    values: formValues
  })

  const onSubmit = async (data: TraderForm) => {
    try {
      if (isNewTrader) {
        await createTrader(data)
      } else {
        await updateTrader(data, traderForm.formState.dirtyFields as TraderFormDirty)
      }
      navigate(`/${ROUTE_TRADER}`)
    } catch (error) {
      console.error('Error saving trader:', error)
      traderForm.clearErrors()
      const validationErrorMap = wireValidationErrorMapFromTrpcError(error)
      if (validationErrorMap) {
        const formErrors = traderWireValidationToFormErrors(validationErrorMap)
        for (const [path, message] of toPairs(formErrors)) {
          if (path === 'root') continue
          traderForm.setError(path as FieldPath<TraderForm>, { message })
        }
        return
      }
      if (isError(error)) {
        traderForm.setError('root', { message: error.message })
      } else {
        traderForm.setError('root', { message: 'Failed to save trader. Please try again.' })
      }
    }
  }

  if (traderLoading) {
    return (
      <div className="flex justify-center items-center h-64 text-muted-foreground">Loading trader information...</div>
    )
  }

  if (traderError) {
    return <div className="flex justify-center items-center h-64 text-red-500">Error: {traderError.message}</div>
  }

  const handleCancel = () => {
    if (isNewTrader) {
      navigate(`/${ROUTE_TRADER}`)
    } else {
      setEditMode(false)
      traderForm.reset(trader ? traderReadToForm(trader) : EMPTY_TRADER_FORM)
    }
  }

  const handleEdit = () => {
    setEditMode(true)
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          {isNewTrader ? 'Create New Trader Organization' : 'Edit Trader Organization'}
        </h1>
        {!isNewTrader && editMode && (
          <button
            className="px-4 py-2 bg-muted text-foreground rounded-md hover:bg-muted/70 flex items-center space-x-2"
            onClick={handleCancel}
          >
            <X />
            <span>Cancel</span>
          </button>
        )}
        {!isNewTrader && !editMode && (
          <button
            className="px-4 py-2 bg-muted text-foreground rounded-md hover:bg-muted/70 flex items-center space-x-2"
            onClick={handleEdit}
          >
            <span>Edit</span>
          </button>
        )}
      </div>

      <div className="bg-background rounded-lg shadow-md p-6">
        <FormProvider {...traderForm}>
          <form onSubmit={traderForm.handleSubmit(onSubmit)} className="space-y-6">
            {traderForm.formState.errors.root && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 rounded-md">
                {traderForm.formState.errors.root.message}
              </div>
            )}

            <TraderOrganizationForm isEditing={editMode} traderId={isNewTrader ? undefined : trader?.id} />

            {kyc?.id && (
              <Card className="border-purple-800 border-2">
                <CardHeader>
                  <CardTitle className="flex flex-row justify-between items-center">
                    <span className="text-lg font-bold">Stripe Metadata</span>
                    <span className="border border-purple-800 rounded-sm p-2 text-purple-800 font-semibold">
                      Read-only from Stripe
                    </span>
                  </CardTitle>
                  <p className="font-semibold">Contact Information</p>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <div>
                    <Label htmlFor="business_type">Doing business as</Label>
                    <Input type="text" name="business_type" disabled={true} value={kyc?.companyName ?? ''} />
                  </div>
                  <div>
                    <Label htmlFor="business_email">Email</Label>
                    <Input type="text" name="business_email" disabled={true} value={kyc?.email ?? ''} />
                  </div>
                  <div>
                    <Label htmlFor="business_website">Website</Label>
                    <Input type="text" name="business_website" disabled={true} value={kyc?.businessProfileUrl ?? ''} />
                  </div>
                  <div>
                    <Label htmlFor="business_phone_number">Phone number</Label>
                    <Input type="text" name="business_phone_number" disabled={true} value={kyc?.companyPhone ?? ''} />
                  </div>
                  <div>
                    <Label htmlFor="business_support_phone_number">Support phone number</Label>
                    <Input
                      type="text"
                      name="business_support_phone_number"
                      disabled={true}
                      value={kyc?.businessProfileSupportPhone ?? ''}
                    />
                  </div>
                  <div className="py-1">
                    <div className="border-t-2 border-border" />
                  </div>
                  <p className="font-semibold">Business Details</p>
                  <div>
                    <Label htmlFor="business_legal_business_name">Legal business name</Label>
                    <Input
                      type="text"
                      name="business_legal_business_name"
                      disabled={true}
                      value={kyc?.companyName ?? ''}
                    />
                  </div>
                  <div>
                    <Label htmlFor="business_statement_descriptor">Statement descriptor</Label>
                    <Input
                      type="text"
                      name="business_statement_descriptor"
                      disabled={true}
                      value={kyc?.statementDescriptor ?? ''}
                    />
                  </div>
                  <div>
                    <Label htmlFor="business_business_type">Business type</Label>
                    <Input type="text" name="business_business_type" disabled={true} value={kyc?.businessType ?? ''} />
                  </div>
                  <div>
                    <Label htmlFor="business_company_structure">Company structure</Label>
                    <Input
                      type="text"
                      name="business_company_structure"
                      disabled={true}
                      value={kyc?.companyStructure ?? ''}
                    />
                  </div>
                  <div>
                    <Label htmlFor="business_industry">Industry</Label>
                    <Input
                      type="text"
                      name="business_industry"
                      disabled={true}
                      value={
                        STRIPE_MCC_MAP[(kyc?.businessProfileMcc ?? '') as keyof typeof STRIPE_MCC_MAP] ?? 'Unknown'
                      }
                    />
                  </div>
                  <div className="py-1">
                    <div className="border-t-2 border-border" />
                  </div>
                  <p className="font-semibold">Organization Address</p>
                  <div>
                    <Label htmlFor="business_industry">Address line 1</Label>
                    <Input
                      type="text"
                      name="business_address_line1"
                      disabled={true}
                      value={kyc?.companyAddressLine1 ?? ''}
                    />
                  </div>
                  <div>
                    <Label htmlFor="business_industry">Address line 2</Label>
                    <Input
                      type="text"
                      name="business_address_line2"
                      disabled={true}
                      value={trader?.address?.line2 ?? ''}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="business_address_city">City</Label>
                      <Input
                        type="text"
                        name="business_address_city"
                        disabled={true}
                        value={trader?.address?.city ?? ''}
                      />
                    </div>
                    <div>
                      <Label htmlFor="business_address_state">State/Province</Label>
                      <Input
                        type="text"
                        name="business_address_state"
                        disabled={true}
                        value={trader?.address?.state ?? ''}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="business_address_postal">Postal code</Label>
                    <Input
                      type="text"
                      name="business_address_postal"
                      disabled={true}
                      value={trader?.address?.postalCode ?? ''}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {!isNewTrader && !editMode && trader && (
              <section>
                <h2 className="text-lg font-medium text-foreground mb-4">Additional Information</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-foreground">ID:</span> {trader.id}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-foreground">Created:</span>{' '}
                    {parseTimestamp(trader.createdAt)?.toKlearDate().toString() ?? 'N/A'}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-foreground">Stripe Account:</span>{' '}
                    {kyc?.id ? 'Yes' : 'No'}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-foreground">Klear Financial Accounts:</span>{' '}
                    {klearAccountsLoading
                      ? 'Loading…'
                      : klearAccounts.length === 0
                        ? 'None'
                        : klearAccounts.map((a) => klearAccountTypeLabel(a.accountType)).join(', ')}
                  </div>
                  {currentUser.isSuperAdmin && !isNewTrader && trader?.organizationId && (
                    <div className="col-span-2 flex flex-wrap gap-2 items-center">
                      <span className="text-sm font-medium text-foreground">Add account:</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isCreatingKlearAccount || hasPayable || !hasReceivable}
                        onClick={() => void createAccount(KLEAR_ACCOUNT_TYPE_PAYABLE)}
                      >
                        {hasPayable ? 'Payable exists' : 'Create Payable'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isCreatingKlearAccount || hasOperational || !hasReceivable}
                        onClick={() => void createAccount(KLEAR_ACCOUNT_TYPE_OPERATIONAL)}
                      >
                        {hasOperational ? 'Operational exists' : 'Create Operational'}
                      </Button>
                      {!hasReceivable && (
                        <span className="text-xs text-muted-foreground">
                          Trader must create a receivable account first.
                        </span>
                      )}
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-foreground">Logo:</span>{' '}
                    {trader.logoUrl ? (
                      <a
                        href={trader.logoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View Logo
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </div>
                  <div className="col-span-2">
                    <span className="text-sm font-medium text-foreground">External Accounts: </span>
                    {externalBankAccounts.length ? (
                      <ul className="list-disc pl-5 mt-1">
                        {externalBankAccounts.map((externalBankAccount: TraderExternalBankAccount) => (
                          <li key={externalBankAccount.id}>
                            {externalBankAccount.bankName}: Routing {externalBankAccount.routingNumber} Account ****
                            {externalBankAccount.last4}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      'None'
                    )}
                  </div>
                </div>
              </section>
            )}
            {editMode && (
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 bg-muted text-foreground rounded-md hover:bg-muted/70"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={traderForm.formState.isSubmitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {traderForm.formState.isSubmitting ? 'Saving...' : isNewTrader ? 'Create Trader' : 'Save Changes'}
                </button>
              </div>
            )}
          </form>
        </FormProvider>
      </div>
    </div>
  )
}

export default TraderDetail
